import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { productAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  Package,
  Euro,
  Hash,
  Search,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';

// Form component outside main component
function ProductForm({ formData, setFormData, productCategories }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Nome Prodotto *</Label>
        <Input
          placeholder="es. Gel Fissante"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label>Descrizione</Label>
        <Textarea
          placeholder="Descrizione del prodotto..."
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Prezzo (€) *</Label>
          <div className="relative">
            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="12.50"
              value={formData.price}
              onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
              className="pl-10"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Quantità in Stock</Label>
          <div className="relative">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="number"
              min="0"
              placeholder="10"
              value={formData.stock}
              onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Categoria</Label>
        <Select
          value={formData.category_id || "none"}
          onValueChange={(v) => setFormData(prev => ({ ...prev, category_id: v === "none" ? "" : v }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Nessuna categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nessuna categoria</SelectItem>
            {productCategories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
        <div>
          <p className="font-medium">Prodotto Attivo</p>
          <p className="text-sm text-muted-foreground">I prodotti disattivati non saranno visibili nello shop</p>
        </div>
        <Switch
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
        />
      </div>
    </div>
  );
}

const ITEMS_PER_PAGE = 12;

export default function ProductManager({ products, categories, onRefresh }) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '0',
    category_id: '',
    is_active: true
  });

  const productCategories = categories.filter(c => c.type === 'product');

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      if (searchTerm && !product.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (categoryFilter !== 'all' && product.category_id !== categoryFilter) {
        return false;
      }
      if (stockFilter === 'in_stock' && product.stock <= 0) {
        return false;
      }
      if (stockFilter === 'low_stock' && (product.stock <= 0 || product.stock > 5)) {
        return false;
      }
      if (stockFilter === 'out_of_stock' && product.stock > 0) {
        return false;
      }
      return true;
    });
  }, [products, searchTerm, categoryFilter, stockFilter]);

  // Paginate
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    return filteredProducts.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );
  }, [filteredProducts, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, stockFilter]);

  const hasActiveFilters = searchTerm || categoryFilter !== 'all' || stockFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setStockFilter('all');
    setCurrentPage(1);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      stock: '0',
      category_id: '',
      is_active: true
    });
  };

  const openCreate = () => {
    resetForm();
    setIsCreateOpen(true);
  };

  const openEdit = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      stock: product.stock?.toString() || '0',
      category_id: product.category_id || '',
      is_active: product.is_active !== false
    });
    setIsEditOpen(true);
  };

  const openDelete = (product) => {
    setSelectedProduct(product);
    setIsDeleteOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.price) {
      toast.error('Nome e prezzo sono obbligatori');
      return;
    }

    setLoading(true);
    try {
      await productAPI.create({
        name: formData.name,
        description: formData.description || null,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock) || 0,
        category_id: formData.category_id || null,
        is_active: formData.is_active
      });
      toast.success('Prodotto creato con successo');
      setIsCreateOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedProduct) return;

    setLoading(true);
    try {
      await productAPI.update(selectedProduct.id, {
        name: formData.name,
        description: formData.description || null,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock) || 0,
        category_id: formData.category_id || null,
        is_active: formData.is_active
      });
      toast.success('Prodotto aggiornato');
      setIsEditOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct) return;

    setLoading(true);
    try {
      await productAPI.delete(selectedProduct.id);
      toast.success('Prodotto eliminato');
      setIsDeleteOpen(false);
      setSelectedProduct(null);
      onRefresh();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || '-';
  };

  const getStockBadge = (stock) => {
    if (stock <= 0) return <Badge variant="destructive" className="text-xs">Esaurito</Badge>;
    if (stock <= 5) return <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">Scarso ({stock})</Badge>;
    return <Badge variant="outline" className="text-xs text-green-600 border-green-600">{stock} disponibili</Badge>;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Gestione Prodotti</CardTitle>
        <Button onClick={openCreate} data-testid="create-product-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Prodotto
        </Button>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4 p-4 bg-muted/50 rounded-lg">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca prodotto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="product-search-input"
              />
            </div>
          </div>
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="product-category-filter">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {productCategories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={stockFilter} onValueChange={setStockFilter}>
            <SelectTrigger className="w-[150px]" data-testid="product-stock-filter">
              <SelectValue placeholder="Stock" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="in_stock">Disponibili</SelectItem>
              <SelectItem value="low_stock">Scorte basse</SelectItem>
              <SelectItem value="out_of_stock">Esauriti</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-product-filters">
              <X className="w-4 h-4 mr-1" />
              Cancella filtri
            </Button>
          )}

          <Badge variant="secondary" className="h-10 px-3 flex items-center">
            {filteredProducts.length} risultati
          </Badge>
        </div>

        {filteredProducts.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            {hasActiveFilters ? 'Nessun prodotto trovato con i filtri selezionati' : 'Nessun prodotto'}
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedProducts.map(product => (
              <Card key={product.id} className={`overflow-hidden ${!product.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Package className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold">{product.name}</p>
                        {product.category_id && (
                          <Badge variant="outline" className="text-xs">{getCategoryName(product.category_id)}</Badge>
                        )}
                      </div>
                    </div>
                    {!product.is_active && (
                      <Badge variant="secondary" className="text-xs">Disattivo</Badge>
                    )}
                  </div>
                  
                  {product.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{product.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-primary">€{product.price?.toFixed(2)}</span>
                      {getStockBadge(product.stock)}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(product)}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDelete(product)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="product-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === currentPage ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  data-testid={`product-page-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="product-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Nuovo Prodotto
            </DialogTitle>
            <DialogDescription>
              Aggiungi un nuovo prodotto al catalogo shop
            </DialogDescription>
          </DialogHeader>
          <ProductForm formData={formData} setFormData={setFormData} productCategories={productCategories} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreate} disabled={loading}>
              {loading ? 'Creazione...' : 'Crea Prodotto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifica Prodotto
            </DialogTitle>
          </DialogHeader>
          <ProductForm formData={formData} setFormData={setFormData} productCategories={productCategories} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdate} disabled={loading}>
              {loading ? 'Salvataggio...' : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription asChild>
              <div>
                Sei sicuro di voler eliminare questo prodotto?
                {selectedProduct && (
                  <div className="mt-2 p-3 bg-muted rounded-md">
                    <p><strong>Nome:</strong> {selectedProduct.name}</p>
                    <p><strong>Prezzo:</strong> €{selectedProduct.price?.toFixed(2)}</p>
                    <p><strong>Stock:</strong> {selectedProduct.stock} unità</p>
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
